import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';
import UploadDocuments from "../components/Dashboard/UploadDocuments";
import { 
  setStepsCompleted, 
  setShowBackToDashboardButton, 
  prepareTaricData, 
  generateTaricJson, 
  toggleCollapseFiles,
  resetDocumentsState, 
  resetSelectedSender, 
  resetSelectedReceiver, 
  resetSelectedAgent,
  setSelectedAgent,
  clearValidationErrors,
  setExtractedData,
  setOcrInputID
} from '../store/reducers';
import { FileCheck, FileJson, RotateCcw } from 'lucide-react';
import { ProcessingModal } from '../components/Dashboard/ProcessingModal';
import { ReviewUserDetails } from '../components/Dashboard/ReviewUserDetails';
import { ReviewReceiverDetails } from "../components/Dashboard/ReviewReceiverDetails";
import { ReviewSenderDetails } from "../components/Dashboard/ReviewSenderDetails";
import { ReviewAgents } from '../components/Dashboard/ReviewAgents';
import { FinalJsonData } from '../components/Dashboard/FinalJsonData';
import { ReviewExtractedData } from '../components/Dashboard/ReviewExtractedData';
import { GeneratedSuccessfullyModal } from '../components/Dashboard/GeneratedSuccessfullyModal';
import DUASpecification from '../components/Dashboard/DUASpecification';
import { scroller } from "react-scroll";
import { useDocumentProcessing } from '../hooks/useDocumentProcessing';
import { useLanguage, getTranslation } from '../hooks/useLanguage';
import CreditSnippet from '../components/CreditSnippet';
import { useCreditStats } from '../hooks/useApiHooks';

import star from '../assets/star.png'

import { useSenders, useAgents, useReceivers } from '../hooks/useApiHooks';
import toast from 'react-hot-toast';
import { useJobStream } from '../hooks/useJobStream';

function Dashboard({ setBacktoDashboardClicked, backtoDashboardClicked, scrollToID, setIsStripeModalOpen }) {
  // Load data from API hooks
  useSenders();
  useReceivers();
  useAgents();
  const { current, usage, exportCost, loading, error, refetch: refetchCreditStats } = useCreditStats();

  // Get state from Redux
  const { documents, hasDocuments, uploadComplete } = useSelector((state) => state.documents);
  const company = useSelector(state => state.company);
  const stepsCompleted = useSelector((state) => state.documents.stepsCompleted);
  
  const userData = useSelector((state) => state.user.userData);
  const receivers = useSelector((state) => state.receivers);
  const senders = useSelector((state) => state.senders);
  const agents = useSelector((state) => state.agents);
  
  // Merge senders and receivers into a single array with type identifiers
  const contacts = [
    ...senders.map(sender => ({ ...sender, contactType: 'sender' })),
    ...receivers.map(receiver => ({ ...receiver, contactType: 'receiver' }))
  ];

  const preparedJson = useSelector((state) => state.preparedJson);
  const taricJsonErrors = useSelector((state) => state.preparedJson.taricJsonErrors);

  const [isReviewConfirmed, setIsReviewConfirmed] = useState(false);
  const [isSuccessfulModalOpen, setIsSuccessfulModalOpen] = useState(false);
  const [specifyAmount, setSpecifyAmount] = useState([]);
  const [additionalContext, setAdditionalContext] = useState('');
  const [activeTab, setActiveTab] = useState('All');

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const selectedSenders = useSelector(state => state.user.selectedSender || []);
  const selectedReceivers = useSelector(state => state.user.selectedReceiver || []);
  const selectedAgents = useSelector(state => state.user.selectedAgent || []);

  const extractedData = useSelector(state => state.documents.extractedData);

  const validationErrors = useSelector((state) => state.validationErrors.errors);

  const {
    isProcessing,
    setIsProcessing,
    isGeneratingTaric,
    hasGenerationError,
    generationErrorMessage,
    processingCancelled,
    setProcessingCancelled,
    processDocuments,
    generateTaricDeclaration
  } = useDocumentProcessing();

  const [isSpanish] = useLanguage();
  
  // Keep stream open for suggestions_ready even after modal closes
  const [activeJobId, setActiveJobId] = useState(null);
  const { status: streamStatus } = useJobStream(activeJobId);

  // Track if this is the first mount to prevent clearing on every render
  const isFirstMountRef = useRef(true);
  
  useEffect(() => {
    dispatch(setShowBackToDashboardButton(false));
    
    // On first mount (page load/refresh), don't clear extractedData
    // Data will be auto-restored from sessionStorage if available
    if (isFirstMountRef.current) {
      isFirstMountRef.current = false;
    }
    
    // Clear originalExtractedData on page load if there's no active job or extractedData
    // BUT preserve it if we have a lastJobId (meaning there's a job we can restore)
    const hasActiveJob = sessionStorage.getItem('activeJobId');
    const hasCompletedJob = sessionStorage.getItem('hasCompletedJob');
    const lastJobId = sessionStorage.getItem('lastJobId');
    const hasExtractedData = extractedData && (Array.isArray(extractedData) ? extractedData.length > 0 : true);
    
    // Fix stuck state: If extractedData exists but stepsCompleted is false, clear extractedData
    // This prevents the stuck state where buttons are disabled but data isn't showing
    if (hasExtractedData && !stepsCompleted) {
      const hasOriginalData = sessionStorage.getItem('originalExtractedData') !== null;
      // Only clear if there's no way to restore (no active job, no completed job, no lastJobId, no originalExtractedData)
      if (!hasActiveJob && !hasCompletedJob && !lastJobId && !hasOriginalData) {
        console.warn('Dashboard: extractedData exists but stepsCompleted is false - clearing extractedData to prevent stuck state');
        dispatch(setExtractedData(null));
        sessionStorage.removeItem('originalExtractedData');
      }
    }
    
    // Only clear if there's no active job, no completed job, no lastJobId, and no extractedData
    // If we have a lastJobId, keep originalExtractedData so restore can work
    if (!hasActiveJob && !hasCompletedJob && !lastJobId && !hasExtractedData) {
      sessionStorage.removeItem('originalExtractedData');
    } else if (lastJobId) {
    }
  }, [dispatch, extractedData, stepsCompleted]);
  
  // Listen for jobId from ProcessingModal and keep stream open
  useEffect(() => {
    const handleJobIdSet = (event) => {
      if (event.detail?.jobId) {
        setActiveJobId(event.detail.jobId);
      }
    };
    
    const handleJobIdClear = () => {
      setActiveJobId(null);
    };
    
    window.addEventListener('setActiveJobId', handleJobIdSet);
    window.addEventListener('clearActiveJobId', handleJobIdClear);
    
    return () => {
      window.removeEventListener('setActiveJobId', handleJobIdSet);
      window.removeEventListener('clearActiveJobId', handleJobIdClear);
    };
  }, []);
  
  // Check for active jobId in sessionStorage on mount
  useEffect(() => {
    const storedJobId = sessionStorage.getItem('activeJobId');
    if (storedJobId) {
      setActiveJobId(storedJobId);
    }
  }, []);

  // Check if restore button should be shown
  const [showRestoreButton, setShowRestoreButton] = useState(false);

  useEffect(() => {
    // Check if user has a job that needs restoration
    // Show restore button if:
    // 1. No extractedData is currently displayed, AND
    // 2. Either lastJobId exists in sessionStorage OR originalExtractedData exists
    const lastJobId = sessionStorage.getItem('lastJobId');
    const hasOriginalExtractedData = sessionStorage.getItem('originalExtractedData') !== null;
    const shouldShowRestore = !extractedData && (lastJobId || hasOriginalExtractedData);
    setShowRestoreButton(shouldShowRestore);
  }, [extractedData]);

  // Function to restore processed data
  const handleRestoreData = async () => {
    try {
      const token = sessionStorage.getItem('token');
      if (!token) {
        toast.error('Please log in to restore data');
        return;
      }

      // Try to get job ID from sessionStorage
      const lastJobId = sessionStorage.getItem('lastJobId');
      
      // First, try to restore from originalExtractedData (fastest, most reliable)
      const originalExtractedDataStr = sessionStorage.getItem('originalExtractedData');
      if (originalExtractedDataStr) {
        try {
          const originalData = JSON.parse(originalExtractedDataStr);
          const isValid = Array.isArray(originalData) 
            ? originalData.length > 0 && originalData.every(d => d !== null && d !== undefined && typeof d === 'object' && Object.keys(d).length > 0)
            : originalData && typeof originalData === 'object' && Object.keys(originalData).length > 0;
          
          if (isValid) {
            dispatch(setExtractedData(originalData));
            dispatch(setStepsCompleted(true));
            // Keep lastJobId so originalExtractedData doesn't get cleared on next render
            // It will be cleared when user starts a new job or explicitly resets
            sessionStorage.removeItem('documentReadyReceived');
            setShowRestoreButton(false);
            toast.success('Processed data restored successfully from saved data');
            return;
          }
        } catch (e) {
        }
      }
      
      // If no originalExtractedData, try to fetch from API
      // Priority: 1) Fetch by job ID (most reliable), 2) /api/queue/current, 3) /api/queue (all jobs)
      let currentJobData = null;
      
      // First, if we have a lastJobId, try fetching that specific job (most reliable)
      if (lastJobId) {
        try {
          const jobResponse = await fetch(`https://${process.env.REACT_APP_DOCS_API_KEY}/api/queue/${lastJobId}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'accept': 'application/json'
            }
          });

          if (jobResponse.ok) {
            currentJobData = await jobResponse.json();
          } else {
            const errorData = await jobResponse.json().catch(() => ({}));
          }
        } catch (e) {
        }
      }
      
      // If fetching by ID didn't work, try /api/queue/current
      if (!currentJobData) {
        try {
          const currentJobResponse = await fetch(`https://${process.env.REACT_APP_DOCS_API_KEY}/api/queue/current`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'accept': 'application/json'
            }
          });

          if (currentJobResponse.ok) {
            currentJobData = await currentJobResponse.json();
          } else {
          }
        } catch (e) {
        }
      }
      
      // If still no job, try getting all jobs and find the most recent completed one
      if (!currentJobData) {
        try {
          const allJobsResponse = await fetch(`https://${process.env.REACT_APP_DOCS_API_KEY}/api/queue`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'accept': 'application/json'
            }
          });

          if (allJobsResponse.ok) {
            const allJobs = await allJobsResponse.json();
            
            // Find the most recent completed job with a result
            if (Array.isArray(allJobs) && allJobs.length > 0) {
              // Sort by updatedAt descending to get most recent first
              const sortedJobs = allJobs.sort((a, b) => {
                const dateA = new Date(a.updatedAt || a.createdAt || 0);
                const dateB = new Date(b.updatedAt || b.createdAt || 0);
                return dateB - dateA;
              });
              
              // Find first completed job with result
              const completedJob = sortedJobs.find(job => 
                job.status === 'completed' && 
                job.result && 
                (Array.isArray(job.result) ? job.result.length > 0 : Object.keys(job.result || {}).length > 0)
              );
              
              if (completedJob) {
                currentJobData = completedJob;
              }
            }
          } else {
          }
        } catch (e) {
        }
      }

      // If we have job data, try to restore from it
      if (currentJobData) {
        // Check for specific error format
        if (currentJobData?.status === 'error' && 
            currentJobData?.code === 'NOT_FOUND') {
          throw new Error('Job not found');
        }

        // If job is completed, restore the data
        if (currentJobData.status === 'completed' && currentJobData.result) {
          // Validate result data structure
          let validResult = null;
          if (Array.isArray(currentJobData.result)) {
            const filtered = currentJobData.result.filter(r => 
              r !== null && r !== undefined && typeof r === 'object' && Object.keys(r).length > 0
            );
            if (filtered.length > 0) {
              validResult = filtered;
            }
          } else if (currentJobData.result && typeof currentJobData.result === 'object' && Object.keys(currentJobData.result).length > 0) {
            validResult = [currentJobData.result];
          }
          
          if (validResult && validResult.length > 0) {
            // Store the restored data in originalExtractedData so it persists
            sessionStorage.setItem('originalExtractedData', JSON.stringify(validResult));
            // Keep lastJobId so originalExtractedData doesn't get cleared on next render
            // It will be cleared when user starts a new job or explicitly resets
            dispatch(setExtractedData(validResult));
            if (validResult[0]?.ocr_input_id) {
              dispatch(setOcrInputID(validResult[0].ocr_input_id));
            }
            dispatch(setStepsCompleted(true));
            
            // Clear the documentReadyReceived flag but keep lastJobId
            sessionStorage.removeItem('documentReadyReceived');
            setShowRestoreButton(false);
            toast.success('Processed data restored successfully');
            return;
          } else {
          }
        } else {
        }
      }

      // If we get here, nothing worked
      throw new Error('No data available to restore');
    } catch (error) {
      sessionStorage.removeItem('lastJobId');
      setShowRestoreButton(false);
      toast.error(error.message || 'No data available to restore');
    }
  };



  useEffect(() => {
    if (backtoDashboardClicked) {
      scroller.scrollTo(scrollToID, {
        duration: 100,     
        offset: -150,
        smooth: 'easeInOutQuint',
        containerId: 'analysis-report'
      });
    }
    setBacktoDashboardClicked(false);
  }, [backtoDashboardClicked, scrollToID, setBacktoDashboardClicked]);

  useEffect(() => {
    if (stepsCompleted) {
      scroller.scrollTo('review-extracted-data', {
        duration: 100,     
        offset: -150,
        smooth: 'easeInOutQuint',
        containerId: 'analysis-report'
      });
    }
  }, [stepsCompleted]);

  const handleResetProcessing = () => {
    setIsProcessing(false);
  };

  const handleGenerateTaric = async () => {
    const result = await generateTaricDeclaration(preparedJson);
    if (result.success) {
      setIsSuccessfulModalOpen(true);
      refetchCreditStats();
    } else {
      setIsSuccessfulModalOpen(true);
    }
  };

  const deleteCurrentJob = async () => {
    try {
      const token = sessionStorage.getItem('token');
      if (!token) {
        return;
      }

      const response = await fetch(`https://${process.env.REACT_APP_DOCS_API_KEY}/api/queue/current`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'accept': 'application/json'
        }
      });

      if (response.ok) {
      } else {
        // If there's no current job (404), that's fine - just log it
        if (response.status === 404) {
        } else {
        }
      }
    } catch (error) {
      // Silently fail - we'll still reload the page
    }
  };

  const handleReset = async () => {
    // Clear all job-related flags when user manually resets
    sessionStorage.removeItem('hasCompletedJob');
    sessionStorage.removeItem('lastJobId');
    sessionStorage.removeItem('originalExtractedData');
    sessionStorage.removeItem('documentReadyReceived');
    // Delete current active job before reloading
    await deleteCurrentJob();
    window.location.reload();
  }


  // Reset active tab when extractedData changes
  useEffect(() => {
    if (extractedData) {
      setActiveTab('All');
    }
  }, [extractedData]);

  // Set default agent (first agent) for each export when extractedData is available
  useEffect(() => {
    // Validate extractedData and agents
    if (!extractedData || !agents || !Array.isArray(agents) || agents.length === 0) {
      return;
    }
    
    // Validate extractedData structure
    const declarations = Array.isArray(extractedData) 
      ? extractedData.filter(d => d !== null && d !== undefined && typeof d === 'object')
      : (extractedData && typeof extractedData === 'object' ? [extractedData] : []);
    
    if (declarations.length === 0) {
      return;
    }
    
    const firstAgent = agents[0];
    if (!firstAgent || typeof firstAgent !== 'object') {
      return;
    }
    
    declarations.forEach((_, index) => {
      // Only set default if no agent is selected for this export index
      if (!selectedAgents[index]) {
        dispatch(setSelectedAgent(firstAgent, index));
      }
    });
  }, [extractedData, agents, dispatch, selectedAgents]);

  useEffect(() => {
    // Check if all exports have senders and receivers
    if (!extractedData) {
      return;
    }
    
    // Validate extractedData structure
    const declarations = Array.isArray(extractedData) 
      ? extractedData.filter(d => d !== null && d !== undefined && typeof d === 'object')
      : (extractedData && typeof extractedData === 'object' ? [extractedData] : []);
    
    if (declarations.length === 0) {
      return;
    }
    
    // Validate validationErrors
    const hasValidationErrors = validationErrors && Array.isArray(validationErrors) && validationErrors.length > 0;
    if (hasValidationErrors) {
      return;
    }
    
    // Validate selectedSenders and selectedReceivers arrays
    const validSelectedSenders = Array.isArray(selectedSenders) ? selectedSenders : [];
    const validSelectedReceivers = Array.isArray(selectedReceivers) ? selectedReceivers : [];
    
    const allHaveSelections = declarations.every((_, index) => 
      validSelectedSenders[index] && 
      validSelectedReceivers[index] &&
      typeof validSelectedSenders[index] === 'object' &&
      typeof validSelectedReceivers[index] === 'object'
    );
    
    if (allHaveSelections && declarations.length > 0) {
      try {
        dispatch(prepareTaricData({
          selectedSenders: validSelectedSenders,
          selectedReceivers: validSelectedReceivers,
          selectedAgents: Array.isArray(selectedAgents) ? selectedAgents : [],
          userData: userData && typeof userData === 'object' ? userData : null,
          extractedData: declarations,
          company: company && typeof company === 'object' ? company : null
        }));
        dispatch(generateTaricJson());
        // Only refetch credits after successful generation, not on every data change
        // Credits will be refetched after actual export/generation completes
      } catch (error) {
      }
    }
  }, [selectedSenders, selectedReceivers, selectedAgents, extractedData, validationErrors, dispatch, userData, company]);

  return (
    <div className="m-0 text-left pb-40">
      {isProcessing && (
        <ProcessingModal
          isProcessing={isProcessing}
          setIsProcessing={setIsProcessing}
          setStepsCompleted={setStepsCompleted}
          onProcessComplete={(onStatusUpdate) => processDocuments(documents, onStatusUpdate, specifyAmount, additionalContext)}
          onCancelProcessing={() => setProcessingCancelled(true)}
        />
      )}
      {isSuccessfulModalOpen && (
        <GeneratedSuccessfullyModal
          setIsSuccessfulModalOpen={setIsSuccessfulModalOpen}
          setIsProcessing={handleResetProcessing}
          setIsReviewConfirmed={setIsReviewConfirmed}
          hasError={hasGenerationError}
          errorMessage={generationErrorMessage}
          taricJsonErrors={taricJsonErrors}
          onRetry={handleGenerateTaric}
        />
      )}
      <div className="mt-20">
        <h1 className="text-5xl font-bold mb-1">{getTranslation('welcome', isSpanish)} {userData?.first_name || "Atlantis Digital"} 👋,</h1>
        <p className="text-gray-500 mt-4">{getTranslation('streamline', isSpanish)}</p>
      </div>
      
        {(!loading && current && current.remainingCredits === 0) ? (
          <div className="flex flex-col font-sans bg-red-50 rounded-xl p-6 mt-20 w-full relative overflow-hidden max-w-2xl">
          <div className="z-1 text-lg font-bold text-red-600 mb-2">
            {getTranslation('noCreditsLeft', isSpanish) || 'You have used up all your remaining credits!'}
          </div>
          <div className="z-1 text-sm text-red-500 mb-4">
            {getTranslation('pleaseBuyCredits', isSpanish) || 'Please buy more credits to continue using the service.'}
          </div>
          <button
            className="z-1 mt-4  w-fit text-center h-10 gap-2 py-2 px-5 rounded-full bg-indigo-100 hover:bg-indigo-200 text-indigo-700 font-semibold"
            onClick={() => setIsStripeModalOpen(true)}
          >
            {getTranslation('buyCredits', isSpanish)}
          </button>
          <img
            src={star}
            alt="Star"
            className="absolute bottom-[-2rem] right-4 size-44 opacity-100 pointer-events-none select-none"
            aria-hidden="true"
          />
        </div>
        ) : (
          <div className="bg-hoverBg rounded-md mt-20 w-full">
          <UploadDocuments />
          </div>
        )}
     
     
      {hasDocuments && uploadComplete && !extractedData && (
        <div className="mt-4 space-y-3">
          <DUASpecification 
            onSpecifyAmountChange={setSpecifyAmount}
            onAdditionalContextChange={setAdditionalContext}
          />
          <div className="flex gap-2 justify-end">
            <button
              className="py-2 px-5 rounded-full bg-default border flex gap-2 items-center"
              onClick={() => 
              {
                setIsProcessing(true)
                dispatch(toggleCollapseFiles())
              }
              }
            >
              <FileCheck className="size-5 stroke-white"/>
              <span className='text-white'>{getTranslation('confirmAndProcess', isSpanish)}</span>
            </button>
          </div>
        </div>
      )}

      {showRestoreButton && !extractedData && (
        <div className="flex gap-2 mt-4 justify-end">
          <button
            className="py-2 px-5 rounded-full bg-default border flex gap-2 items-center"
            onClick={handleRestoreData}
          >
            <FileCheck className="size-5 stroke-white"/>
            <span className='text-white'>{getTranslation('restore', isSpanish) || 'Restore Processed Data'}</span>
          </button>
        </div>
      )}

      {stepsCompleted && (
        <>
          <ReviewExtractedData jsonData={extractedData}/>
          <ReviewUserDetails userData={userData} navigate={navigate} />

          {/* Tabs for each export/declaration */}
          {extractedData && (Array.isArray(extractedData) ? extractedData.length > 0 : true) && (
            <div className="mt-8">
              {/* Tab Navigation */}
              <div className="pt-2 mt-4 overflow-x-auto mb-4">
                <div className="flex gap-2 items-center pb-2">
                  {/* All tab */}
                  <button
                    onClick={() => setActiveTab('All')}
                    className={`h-10 rounded-full text-sm cursor-pointer hover:opacity-75 transition-opacity py-1 whitespace-nowrap flex-shrink-0 ${
                      activeTab === 'All'
                        ? 'bg-secondary font-semibold px-4'
                        : 'px-2'
                    }`}
                  >
                    {isSpanish ? 'Todo' : 'All'}
                  </button>
                  
                  {/* Individual export tabs */}
                  {(Array.isArray(extractedData) ? extractedData : [extractedData]).map((declaration, index) => (
                    <button
                      key={index}
                      onClick={() => setActiveTab(index)}
                      className={`h-10 rounded-full text-sm cursor-pointer hover:opacity-75 transition-opacity py-1 whitespace-nowrap flex-shrink-0 ${
                        activeTab === index
                          ? 'bg-secondary font-semibold px-4'
                          : 'px-2'
                      }`}
                    >
                      {isSpanish ? `Exportación #${index + 1}` : `Export #${index + 1}`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Content for active tab */}
              {activeTab === 'All' ? (
                // Show all exports
                <div className="space-y-8">
                  {(Array.isArray(extractedData) ? extractedData : [extractedData]).map((declaration, index) => (
                    <div key={index} className="">
                      <h3 className="text-template-primary text-2xl font-semibold mb-4 uppercase">
                        {isSpanish ? `Exportación #${index + 1}` : `Export #${index + 1}`}
                      </h3>
                      <div className='review-container grid gap-4 grid-cols-3'>
                      <ReviewAgents 
                          agents={agents} 
                          navigate={navigate} 
                          userData={userData} 
                          declarationIndex={index}
                        />
                        <ReviewSenderDetails 
                          contacts={contacts} 
                          userData={userData} 
                          navigate={navigate} 
                          declarationIndex={index}
                        />
                        <ReviewReceiverDetails 
                          contacts={contacts} 
                          navigate={navigate} 
                          userData={userData} 
                          declarationIndex={index}
                        />
                       
                      </div>
                    </div>
                  ))}
                </div>
              ) : (

                <>
                <h3 className="text-template-primary text-2xl font-semibold mb-4 uppercase">
                {isSpanish ? `Exportación #${activeTab + 1}` : `Export #${activeTab + 1}`}
                {extractedData[activeTab].expediente && (
                  <span className="ml-2 text-sm text-gray-500 font-normal">({extractedData[activeTab].expediente})</span>
                )}
              </h3>
                <div className='review-container grid gap-4 grid-cols-3 mt-4'>
                  <ReviewAgents 
                    agents={agents} 
                    navigate={navigate} 
                    userData={userData} 
                    declarationIndex={activeTab}
                  />
                  <ReviewSenderDetails 
                    contacts={contacts} 
                    userData={userData} 
                    navigate={navigate} 
                    declarationIndex={activeTab}
                  />
                  <ReviewReceiverDetails 
                    contacts={contacts} 
                    navigate={navigate} 
                    userData={userData} 
                    declarationIndex={activeTab}
                  />
                </div>
                </>
              )}
            </div>
          )}

          {(!validationErrors || validationErrors.length === 0) && (
          
          <div id='json-container'>
            {extractedData && (() => {
              const declarations = Array.isArray(extractedData) ? extractedData : [extractedData];
              const allHaveSelections = declarations.every((_, index) => 
                selectedSenders[index] && selectedReceivers[index]
              );
              return allHaveSelections;
            })() && (
              <>
                <FinalJsonData jsonData={preparedJson.taricJson} />
                <div className="flex gap-2 mt-4 justify-end">
                <button
                    className="flex items-center gap-2 py-2 px-5 rounded-full bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleReset}
                  >
                    <RotateCcw className="size-5 stroke-red-500"/>
                    <span className='text-red-500'>
                    {getTranslation('reset', isSpanish)}
                    </span>
                  </button>
                  <button
                    className="flex items-center gap-2 py-2 px-5 rounded-full bg-default border disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleGenerateTaric}
                    disabled={isGeneratingTaric}
                  >
                    <FileJson className="size-5 stroke-white"/>
                    <span className='text-white'>
                    {isGeneratingTaric 
                      ? getTranslation('generatingDeclaration', isSpanish)
                      : hasGenerationError 
                        ? getTranslation('retryDeclaration', isSpanish)
                        : getTranslation('generateDeclaration', isSpanish)}
                    </span>
                  </button>
                 
                </div>
              </>
            )}
          </div>

)}
        </>
      )}
    </div>
  );
}

export default Dashboard;